import type { AuthenticationResult } from "@azure/msal-browser-1p";
import type { MailboxInfo } from "owa-client-types";
import { logStartUsage } from "owa-analytics-start";
/**
 * Validates that the token returned by MSAL belongs to the expected user identity.
 *
 * NOTE: Currently observe-only — mismatches are logged but not acted upon.
 * Once telemetry confirms the login_hint rollout is complete, this will be
 * extended to reject mismatched tokens.
 *
 * @param authResult - The authentication result from MSAL
 * @param mailboxInfo - The mailbox we expected the token for
 * @param requestedLoginHint - The login_hint that was passed to the MSAL API call
 */
export function validateIdTokenIdentity(authResult: AuthenticationResult, mailboxInfo: MailboxInfo, requestedLoginHint?: string): void {
    if (!authResult.account) {
        return; // No account info means there is no server-returned identity to compare.
    }
    
    const returnedLoginHint = authResult.account.idTokenClaims?.login_hint;
    const returnedUsername = authResult.account.username;
    
    // Validation only makes sense when the caller supplied the login_hint used for token acquisition.
    if (!requestedLoginHint) {
        return;
    }
    
    // Distinguish between UPN/email-style hints and opaque login_hint values.
    // UPNs can be safely compared against the account username as a fallback.
    // Opaque hints can only be compared against the returned login_hint claim.
    const requestedHintIsUpn = requestedLoginHint.includes("@");
    const returnedUsernameIsUpn = returnedUsername?.includes("@") ?? false;
    
    // If the request used an opaque hint and the token did not echo back a login_hint claim,
    // we cannot prove either a match or a mismatch, so keep this as observe-only and return.
    if (!requestedHintIsUpn && returnedLoginHint == null) {
        return;
    }
    
    // Treat the token as valid when either:
    // 1. The returned login_hint exactly matches what we requested, or
    // 2. The requested hint is a UPN/email and the returned username matches it case-insensitively.
    const isMatch = (returnedLoginHint !== undefined && returnedLoginHint === requestedLoginHint) ||
        (requestedHintIsUpn &&
            returnedUsernameIsUpn &&
            returnedUsername !== undefined &&
            returnedUsername.toLowerCase() === requestedLoginHint.toLowerCase());
    
    if (!isMatch) {
        // This path is intentionally telemetry-only for now.
        // Log presence/shape signals without logging raw identity values to avoid PII leakage.
        logStartUsage("MsalTokenIdentityMismatch", mailboxInfo, {
            hasReturnedLoginHint: !!returnedLoginHint,
            hasReturnedUsername: !!returnedUsername,
            hasMailboxInfo: !!mailboxInfo,
            // Log whether the hint looks like a UPN (contains @) vs opaque login_hint
            requestedHintIsUpn,
        });
    }
}
